import React, { useEffect, useMemo, useState } from "react"
import {
  faCheck,
  faChevronRight,
  faChevronUp,
  faPen,
  faTrash,
  faWarning,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Divider, Form, message, Radio, Switch, Tooltip } from "antd"
import { DisplayProvider } from "@/UI"
import { useTranslation } from "react-i18next"
import { formatMultipleServerDeliveryTypes } from "@/utils/providers"
import {
  IOrganizationProvider,
  IProviderDetails,
  OrgProviderCredentials,
} from "@/types/organization-providers"
import { isEmpty } from "lodash"
import { putOrganizationProvidersCredentials } from "@/api/organization-providers"
import ProviderAuthInputs from "./ProviderAuthInputs"
import ApiSettingsTooltip from "./ApiSettingsTooltip"
import DeleteCredentialsModal from "./DeleteCredentialsModal"
import DisplayAvailableVehicles from "@/UI/DataDisplay/DisplayAvailableVehicles"

import { localizationAtom } from "@/atoms/localizationAtom"
import { useRecoilValue } from "recoil"
import useMediaQuery from "@/utils/useMediaQuery"
import DisplayPrice from "@/UI/DataDisplay/DisplayPrice"
import { getCountryFeeStart } from "@/utils/tips"
import { userAtom } from "@/atoms/userAtom"
import { currencyAtom } from "@/atoms/currencyAtom"
import { useFlags } from "flagsmith/react"

interface IProviderTableRowProps {
  id: string
  isLoading: boolean
  isProviderEnabled: boolean
  isDisabled: boolean
  providerDetails: IProviderDetails
  handleToggleProvider: () => void
  orgProvider?: IOrganizationProvider
  updateSingleOrganizationProvider: (orgProvider: IOrganizationProvider) => void
  removeOrgProvider: (id: string) => Promise<void>
}

export const ProviderTableRowHeader: React.FC<{}> = () => {
  const { t } = useTranslation()
  return (
    <RowContainer id="provider-table-header">
      <div className="grid w-full grid-cols-12 gap-4 text-gray-600 font-normal">
        <div className="col-span-1 break-words uppercase">
          {t("profile.ProviderSettings.enabled")}
        </div>
        <div className="col-span-2 break-words uppercase">{t("common.couriers")}</div>
        <div className="col-span-2 break-words uppercase">
          {t("profile.ProviderSettings.deliveryTypes")}
        </div>
        <div className="col-span-2 break-words uppercase">
          {t("profile.ProviderSettings.vehicles")}
        </div>
        <div className="col-span-1 break-words uppercase">
          <Tooltip
            title={t("profile.ProviderSettings.podAvailableDesc")}
            placement="bottomRight"
            arrow={{ pointAtCenter: true }}
          >
            {t("profile.ProviderSettings.podAvailable")}
          </Tooltip>
        </div>
        <div className="col-span-1 break-words uppercase">
          <Tooltip
            title={t("profile.ProviderSettings.codAvailableDesc")}
            placement="bottomRight"
            arrow={{ pointAtCenter: true }}
          >
            {t("profile.ProviderSettings.codAvailable")}
          </Tooltip>
        </div>
        <div className="col-span-2 break-words uppercase">{t("profile.ProviderSettings.fees")}</div>
      </div>
    </RowContainer>
  )
}

const RowContainer: React.FC<{ children: React.ReactNode; id?: string }> = ({ children, id }) => {
  return (
    <div id={id} className="flex flex-col p-3 border rounded-md">
      {children}
    </div>
  )
}

export const ProviderDetailsRow: React.FC<IProviderTableRowProps> = ({
  id,
  isLoading,
  isProviderEnabled,
  isDisabled,
  providerDetails,
  handleToggleProvider,
  orgProvider,
  updateSingleOrganizationProvider,
  removeOrgProvider,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const isDesktop = useMediaQuery("(min-width: 892px)")
  const { locale } = useRecoilValue(localizationAtom)
  const { org } = useRecoilValue(userAtom)
  const { code } = useRecoilValue(currencyAtom)
  const flags = useFlags(["kosmo_api_keys_authorized"])

  const [isCredentialsLoading, setIsCredentialsLoading] = useState<boolean>(false)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [apiMode, setApiMode] = useState<1 | 2>(isEmpty(orgProvider?.credentials) ? 1 : 2)

  const organizationCredentials = orgProvider?.credentials
  useEffect(() => {
    form.setFieldsValue({
      ...organizationCredentials,
    })
    if (!isEmpty(organizationCredentials)) {
      setApiMode(2)
    }
  }, [organizationCredentials])

  const handleCredentialsSubmit = async (formValues: OrgProviderCredentials) => {
    try {
      setIsCredentialsLoading(true)
      const credentials = formValues
      // TODO: To be typed
      const hasMarketConfig = !!providerDetails?.authConfig?.fields?.find(
        (field) => field.key === "market"
      )

      if (hasMarketConfig) {
        credentials.market = locale.toUpperCase()
      }

      const { data } = await putOrganizationProvidersCredentials({
        credentials,
        provider: providerDetails.name,
        id: orgProvider?.id,
      })
      updateSingleOrganizationProvider(data)
      setIsEditing(false)
      message.success("Successfully updated your provider credentials")
    } catch (error) {
      console.log(error + " updating provider credentials")
      message.error("An error occured while updating your provider credentials")
    } finally {
      setIsCredentialsLoading(false)
    }
  }

  const handleRemoveCredentials = async (id: string) => {
    try {
      await removeOrgProvider(id)
      form.resetFields()
      setApiMode(1)
      message.success("Successfully unlinked your account")
    } catch (error) {
      message.error("Error unlinking your account")
    }
  }

  const handleExpandCollapse = () => {
    setIsExpanded(!isExpanded)
  }

  const feeStartAmount = useMemo(
    () => getCountryFeeStart(org?.country, providerDetails),
    [org?.country, providerDetails]
  )

  return (
    <RowContainer id={id}>
      <div className="grid w-full grid-cols-12 gap-4 flex-wrap items-center select-none">
        <div className="col-span-1 flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={isProviderEnabled}
            onChange={handleToggleProvider}
            loading={isLoading}
            disabled={isDisabled}
          />
          {!flags.kosmo_api_keys_authorized.enabled && isProviderEnabled && apiMode === 1 && (
            <Tooltip
              title={
                <div>
                  <p>{t("profile.ProviderSettings.useKosmoApiKeyDisabled")}</p>
                </div>
              }
            >
              <p>
                <FontAwesomeIcon icon={faWarning} className="text-yellow-500" />
              </p>
            </Tooltip>
          )}
        </div>
        <div className="col-span-2 flex justify-start" id="provider-name">
          <DisplayProvider provider={providerDetails.name} showName={isDesktop} />
        </div>
        <div className="col-span-2" id="provider-delivery-types">
          {formatMultipleServerDeliveryTypes(providerDetails.deliveryTypes, t)}
        </div>
        <div className="col-span-2" id="provider-vehicles">
          <DisplayAvailableVehicles vehicles={providerDetails.vehicles} />
        </div>
        <div className="col-span-1" id="provider-pod">
          <FontAwesomeIcon
            className={`flex-shrink-0 h-5 ml-2 ${
              providerDetails.isPodAvailable ? "text-green-500" : "text-red-500"
            }`}
            icon={providerDetails.isPodAvailable ? faCheck : faXmark}
          />
        </div>
        <div className="col-span-1" id="provider-pod">
          <FontAwesomeIcon
            className={`flex-shrink-0 h-5 ml-2 ${
              providerDetails.isCodAvailable ? "text-green-500" : "text-red-500"
            }`}
            icon={providerDetails.isCodAvailable ? faCheck : faXmark}
          />
        </div>
        <div className="col-span-2" id="provider-vehicles">
          {feeStartAmount && (
            <span className="flex">
              <span className="mr-1">{t("profile.ProviderSettings.from")}</span>
              <DisplayPrice amount={feeStartAmount} currencyCode={code} justify="start" isCents />
            </span>
          )}
        </div>
        <div
          className={`col-span-1 text-center cursor-pointer details-${
            isExpanded ? "open" : "closed"
          }`}
          id="open-provider-details"
          onClick={handleExpandCollapse}
        >
          <FontAwesomeIcon
            className="flex-shrink-0"
            icon={isExpanded ? faChevronUp : faChevronRight}
          />
        </div>
      </div>
      {isExpanded && (
        <>
          <div className="grid w-full grid-cols-12 gap-4">
            {/* 12 columns grid to integrate 1 column offset on expanded block*/}
            <div className="col-start-2 col-span-12 space-y-1">
              <Divider
                style={{
                  width: "80% !important",
                  minWidth: "80% !important",
                }}
              />
              <Form form={form} onFinish={handleCredentialsSubmit}>
                {/*  11 columns grid to maintain alignment including the 1col offset*/}
                <div className="grid w-full grid-cols-11 gap-x-4 mb-4">
                  <div className="flex col-span-10 justify-between">
                    <ApiSettingsTooltip />
                    {apiMode === 1 && !isEmpty(organizationCredentials) && orgProvider?.id && (
                      <DeleteCredentialsModal
                        onConfirm={() => handleRemoveCredentials(orgProvider?.id)}
                        button={
                          <Button type="link" ghost className="ml-auto px-0 py-0">
                            <span className="hover:underline">{t("common.save")}</span>
                          </Button>
                        }
                      />
                    )}
                  </div>
                  <div className="col-span-5">
                    <div className="flex justify-between">
                      <div className="flex items-center" style={{ minHeight: 32 }}>
                        <Radio
                          value={1}
                          checked={apiMode === 1}
                          onChange={(e) => setApiMode(e.target.value)}
                          disabled={!flags.kosmo_api_keys_authorized.enabled}
                        >
                          {t("profile.ProviderSettings.useKosmoApiKey")}
                        </Radio>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-5 space-y-3" id="provider-api-keys-container">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center" style={{ minHeight: 32 }}>
                        <Radio
                          checked={apiMode === 2}
                          value={2}
                          onChange={(e) => setApiMode(e.target.value)}
                        >
                          {t("profile.ProviderSettings.yourOwnApiKeys")}
                        </Radio>
                      </div>
                      {/* Not editing mode */}
                      {apiMode === 2 && !isEditing && (
                        <>
                          {isEmpty(organizationCredentials) ? (
                            <Button
                              id="save-provider-settings"
                              type="text"
                              htmlType="submit"
                              // Temporary until pandago is enabled
                              disabled={isCredentialsLoading}
                              loading={isCredentialsLoading}
                              className="text-kosmoBlue hover:text-kosmoBlue"
                            >
                              <span className="!h-fit">{t("common.save")}</span>
                            </Button>
                          ) : (
                            <>
                              {orgProvider?.id && (
                                <div className="flex space-x-3">
                                  <div
                                    className="flex items-center justify-center rounded-full bg-gray-200 w-8 h-8 cursor-pointer text-gray-400 hover:text-neutral-800"
                                    onClick={() => setIsEditing(true)}
                                  >
                                    <FontAwesomeIcon icon={faPen} />
                                  </div>
                                  <DeleteCredentialsModal
                                    onConfirm={() => handleRemoveCredentials(orgProvider.id)}
                                    button={
                                      <div className="flex items-center justify-center rounded-full bg-gray-200 w-8 h-8 cursor-pointer text-gray-400 hover:text-neutral-800">
                                        <FontAwesomeIcon icon={faTrash} />
                                      </div>
                                    }
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                      {/* Edition mode buttons */}
                      {apiMode === 2 && isEditing && (
                        <div className="flex space-x-2">
                          <Button
                            type="link"
                            ghost
                            className="!px-0 py-0"
                            onClick={() => setIsEditing(false)}
                          >
                            <span className="hover:underline !h-fit text-gray-400">
                              {t("common.cancel")}
                            </span>
                          </Button>
                          <Button
                            type="link"
                            ghost
                            className="!px-0 py-0"
                            htmlType="submit"
                            disabled={isCredentialsLoading}
                            loading={isCredentialsLoading}
                          >
                            <span className="hover:underline !h-fit">{t("common.save")}</span>
                          </Button>
                        </div>
                      )}
                    </div>
                    {apiMode === 2 && (
                      <ProviderAuthInputs
                        isEditing={isEditing}
                        authFields={providerDetails.authConfig?.fields}
                        organizationCredentials={organizationCredentials}
                      />
                    )}
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </>
      )}
    </RowContainer>
  )
}
